import { createSlice } from '@reduxjs/toolkit'

const initialState = {
    // Loading states
    isLoading: true,
    calculating: false,
    saving: false,
    mounted: false,

    // Tab navigation
    selectedTab: 0,

    // Error dialog
    errOpen: false,
    errText: '',
    errTitle: '',
    hasError: false,
    error: '',

    // Popover
    popover: null,

    // Page state
    page: 0,

    // Feature toggles
    showSetup: true,
    createDocuments: false,

    // Presentation
    slides: [],
    fileList: [],
    presentationVersions: [],
    selectedPresentation: '',

    // Agreement/Confirmation flags
    agreementConfirmSend: false,
    birdProtectionConfirmSend: false,
    agreementData: {},

    // Redux save trigger
    saveRedux: false
}

const uiSlice = createSlice({
    name: 'ui',
    initialState,
    reducers: {
        setIsLoading: (state, action) => {
            state.isLoading = action.payload
        },
        setCalculating: (state, action) => {
            state.calculating = action.payload
        },
        setSaving: (state, action) => {
            state.saving = action.payload
        },
        setMounted: (state, action) => {
            state.mounted = action.payload
        },
        setSelectedTab: (state, action) => {
            state.selectedTab = action.payload
        },
        showError: (state, action) => {
            state.errOpen = true
            state.errText = action.payload.text || ''
            state.errTitle = action.payload.title || 'Error'
            state.hasError = true
            state.error = action.payload.text || ''
        },
        clearError: (state) => {
            state.errOpen = false
            state.errText = ''
            state.errTitle = ''
            state.hasError = false
            state.error = ''
        },
        setErrOpen: (state, action) => {
            state.errOpen = action.payload
        },
        setPopover: (state, action) => {
            state.popover = action.payload
        },
        setPage: (state, action) => {
            state.page = action.payload
        },
        setShowSetup: (state, action) => {
            state.showSetup = action.payload
        },
        setCreateDocuments: (state, action) => {
            state.createDocuments = action.payload
        },
        setSlides: (state, action) => {
            state.slides = action.payload
        },
        setFileList: (state, action) => {
            state.fileList = action.payload
        },
        setPresentationVersions: (state, action) => {
            state.presentationVersions = action.payload
        },
        setSelectedPresentation: (state, action) => {
            state.selectedPresentation = action.payload
        },
        setAgreementConfirmSend: (state, action) => {
            state.agreementConfirmSend = action.payload
        },
        setBirdProtectionConfirmSend: (state, action) => {
            state.birdProtectionConfirmSend = action.payload
        },
        setAgreementData: (state, action) => {
            state.agreementData = action.payload
        },
        setSaveRedux: (state, action) => {
            state.saveRedux = action.payload
        },

        // Bulk update from quote data
        updateUIFromQuote: (state, action) => {
            const data = action.payload
            if (data.slides !== undefined) state.slides = data.slides
            if (data.fileList !== undefined) state.fileList = data.fileList
            if (data.filelist !== undefined) state.fileList = data.filelist
            if (data.presentationVersions !== undefined) state.presentationVersions = data.presentationVersions
            if (data.presentation_versions !== undefined) state.presentationVersions = data.presentation_versions
            if (data.selectedPresentation !== undefined) state.selectedPresentation = data.selectedPresentation
            if (data.selected_presentation !== undefined) state.selectedPresentation = data.selected_presentation
            if (data.agreementData !== undefined) state.agreementData = data.agreementData
            if (data.agreementdata !== undefined) state.agreementData = data.agreementdata
            if (data.createDocuments !== undefined) state.createDocuments = data.createDocuments
            if (data.create_documents !== undefined) state.createDocuments = data.create_documents
        },

        resetUI: () => initialState
    }
})

export const {
    setIsLoading,
    setCalculating,
    setSaving,
    setMounted,
    setSelectedTab,
    showError,
    clearError,
    setErrOpen,
    setPopover,
    setPage,
    setShowSetup,
    setCreateDocuments,
    setSlides,
    setFileList,
    setPresentationVersions,
    setSelectedPresentation,
    setAgreementConfirmSend,
    setBirdProtectionConfirmSend,
    setAgreementData,
    setSaveRedux,
    updateUIFromQuote,
    resetUI
} = uiSlice.actions

// Selectors
export const selectIsLoading = (state) => state.ui.isLoading
export const selectCalculating = (state) => state.ui.calculating
export const selectSaving = (state) => state.ui.saving
export const selectMounted = (state) => state.ui.mounted
export const selectSelectedTab = (state) => state.ui.selectedTab
export const selectErrOpen = (state) => state.ui.errOpen
export const selectErrText = (state) => state.ui.errText
export const selectErrTitle = (state) => state.ui.errTitle
export const selectHasError = (state) => state.ui.hasError
export const selectError = (state) => state.ui.error
export const selectPopover = (state) => state.ui.popover
export const selectPage = (state) => state.ui.page
export const selectShowSetup = (state) => state.ui.showSetup
export const selectCreateDocuments = (state) => state.ui.createDocuments
export const selectSlides = (state) => state.ui.slides
export const selectFileList = (state) => state.ui.fileList
export const selectPresentationVersions = (state) => state.ui.presentationVersions
export const selectSelectedPresentation = (state) => state.ui.selectedPresentation
export const selectAgreementConfirmSend = (state) => state.ui.agreementConfirmSend
export const selectBirdProtectionConfirmSend = (state) => state.ui.birdProtectionConfirmSend
export const selectAgreementData = (state) => state.ui.agreementData
export const selectSaveRedux = (state) => state.ui.saveRedux

export default uiSlice.reducer
